import React, { useEffect, useState } from 'react';
import { Button, Table, Header, Icon, Input, Message } from 'semantic-ui-react';
import AddTemplate from './AddTemplate';
import UpdateTemplate from './UpdateTemplate';
import { toast } from '../../../common/Toast';
import LocalStorage from '../../../../lib/LocalStorage';
import config from '../../../../common/repository/config';

interface Template {
    id: string;
    name: string;
    status:string;
    message_subject: string;
    message: string;
    created_on: string;
    modified_on: string;
    type?: string;
    placeholder?: string[];
}

const AddedTemplateList: React.FC = () => {
    const [showForm, setShowForm] = useState(false);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [noDataMessage, setNoDataMessage] = useState('');
    const [reloadFlag, setReloadFlag] = useState(false);

    const fetchTemplates = async (name?: string) => {
        setLoading(true);
        setNoDataMessage('');
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const payload = name ? { name } : {};

            const response = await fetch(`${config.apiBaseURL}/template`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();
            setTemplates(result.templates || []);

            if (!response.ok) {
                const errorMessage = result?.message || 'Failed to fetch templates.';
                setTemplates([]);
                setNoDataMessage(errorMessage);
                return;
            }

            const items: Template[] = result?.data?.items || [];
            if (items.length === 0) {
                setNoDataMessage(result?.message || 'No templates found.');
            }
            setTemplates(items);
        } catch (error) {
            console.error('Fetch error:', error);
            toast.error('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, [reloadFlag]);

    const handleSearch = () => {
        fetchTemplates(searchTerm.trim());
    };

    const handleBack = () => {
        setShowForm(false);
        setEditingTemplate(null);
        fetchTemplates();
    };

    const handleDelete = async (template: Template) => {
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/${template.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            if (!response.ok) {
                toast.error(result?.message || 'Failed to delete template.');
                return;
            }

            toast.success(result?.message || 'Template deleted successfully');
            fetchTemplates();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Error deleting template');
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateAdded = () => {

        setReloadFlag(prev => !prev);
    };


    return (
        <div style={{ padding: '2rem' }}>
            {/* Top Controls */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    flexWrap: 'wrap',
                    gap: '1rem'
                }}
            >
                {/* Left: Add Template Button */}
                <div>
                    <Button
                        primary
                        onClick={() => {
                            setShowForm(!showForm);
                            setEditingTemplate(null);
                        }}
                    >
                        <Icon name={showForm ? 'arrow left' : 'plus'} />
                        {showForm ? 'Back to List' : 'Add Template'}
                    </Button>
                </div>

                {/* Right: Filters/Search */}
                {!showForm && (
                    <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <Input
                            placeholder="Search by name..."
                            icon="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e: any) => {
                                if (e.key === 'Enter') handleSearch();
                            }}
                            style={{ minWidth: '220px' }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: 'white', borderRadius: '25px', padding: '5px 20px' }}>
                            <Button
                                color="green"
                                onClick={handleSearch}
                                loading={loading}
                                style={{
                                    borderRadius: '50px 0 0 50px',
                                    padding: '10px 20px',
                                    height: 'auto',
                                }}
                            >
                                Search
                            </Button>
                            <span style={{ padding: '0 10px', fontWeight: 'bold' }}>or</span>
                            <Button
                                onClick={() => {
                                    setSearchTerm('');

                                }}
                                style={{
                                    borderRadius: '0 50px 50px 0',
                                    padding: '10px 20px',
                                    height: 'auto',
                                }}
                            >
                                Clear
                            </Button>
                        </div>

                    </div>
                )}
            </div>

            {/* Main Content */}
            {!showForm ? (
                <>
                    {noDataMessage ? (
                        <Message warning icon>
                            <Message.Content>
                                <Message.Header style={{ textAlign: 'center' }}>
                                    {noDataMessage}
                                </Message.Header>
                            </Message.Content>
                        </Message>
                    ) : (
                        <Table celled striped selectable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell width={1}>Template Name</Table.HeaderCell>
                                    <Table.HeaderCell width={1}>Status</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Subject</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Message</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Type</Table.HeaderCell>
                                    <Table.HeaderCell width={2}>Placeholder</Table.HeaderCell>
                                    <Table.HeaderCell width={2} textAlign="center">
                                        Actions
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {templates.map((template) => (
                                    <Table.Row key={template.id}>
                                        <Table.Cell>{template.name}</Table.Cell>
                                        <Table.Cell>{template.status}</Table.Cell>
                                        <Table.Cell>{template.message_subject}</Table.Cell>
                                        <Table.Cell
                                            style={{ wordBreak: "break-word", maxHeight: "150px", overflowY: "auto", whiteSpace: "normal", display: "block" }}>{template.message}
                                        </Table.Cell>
                                        <Table.Cell>{template.type}</Table.Cell>
                                        <Table.Cell>{template.placeholder?.join(', ')}</Table.Cell>


                                        <Table.Cell textAlign="center">
                                            <Button
                                                size="small"
                                                color="blue"
                                                onClick={() => {
                                                    setEditingTemplate(template);
                                                    setShowForm(true);
                                                }}
                                                style={{ marginRight: '0.5rem' }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="red"
                                                onClick={() => handleDelete(template)}
                                            >
                                                Delete
                                            </Button>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    )}
                </>
            ) : editingTemplate ? (
                <UpdateTemplate template={editingTemplate} onBack={handleBack} />
            ) : (
                <AddTemplate onBack={handleBack} />
            )}
        </div>
    );



};

export default AddedTemplateList;
